// Phase I3 — Intelligence Stack (the Bet Builder leg list).
// Faithful port of prototype/super-acca.jsx::SuperAccaA legs panel — uses
// the I0 LegRow primitive but expands inline for the model-vs-market bar
// and the 2-sentence reasoning drawer (kept here, not pushed down into
// the primitive, so the primitive stays reusable elsewhere).

import { useState } from "react";
import { summarizeReasoning } from "../../../agents/reasoning.ts";
import { Badge, CategoryChip, Eyebrow } from "../../../components/super-acca";
import { layerTone } from "../../../super-acca/categories";
import type { SuperAccaLeg, SuperAccaSlip } from "../../../super-acca/view-models";

export interface IntelligenceStackProps {
  slip: SuperAccaSlip;
}

export function IntelligenceStack({ slip }: IntelligenceStackProps) {
  const [openLeg, setOpenLeg] = useState<number>(0);

  return (
    <div
      style={{
        marginTop: 32,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
        padding: 26,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Eyebrow>Bet Builder · {slip.legs.length} Legs</Eyebrow>
          <div
            style={{
              fontFamily: "var(--sa-font-display)",
              fontSize: 26,
              fontWeight: 600,
              marginTop: 8,
              color: "var(--sa-fg-primary)",
            }}
          >
            Intelligence Stack
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
          <span
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.14em",
            }}
          >
            COMBINED ODDS
          </span>
          <span
            style={{
              fontFamily: "var(--sa-font-stat)",
              fontSize: 44,
              color: "var(--sa-accent-saffron)",
              lineHeight: 1,
            }}
          >
            {slip.combined_odds.toFixed(2)}×
          </span>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        {slip.legs.map((leg, i) => (
          <LegRow
            key={leg.id}
            leg={leg}
            index={i}
            isOpen={openLeg === i}
            onToggle={() => setOpenLeg(openLeg === i ? -1 : i)}
          />
        ))}
      </div>

      <div
        style={{
          paddingTop: 18,
          marginTop: 8,
          borderTop: "1px solid var(--sa-hairline)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-secondary)",
          }}
        >
          GH₵100 returns{" "}
          <span style={{ color: "var(--sa-accent-saffron)" }}>
            GH₵{Math.round(100 * slip.combined_odds).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

function LegRow({
  leg,
  index,
  isOpen,
  onToggle,
}: {
  leg: SuperAccaLeg;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const tone = layerTone(leg.layer);
  const reasoningLong = summarizeReasoning(leg.reasoning_long ?? leg.reasoning_short);
  return (
    <div
      style={{
        borderTop: "1px solid var(--sa-hairline)",
        borderBottom: leg.anchor ? "2px solid var(--sa-accent-saffron)" : "none",
        padding: "20px 0",
        background: leg.anchor
          ? "linear-gradient(90deg, rgba(212,168,67,0.04), transparent 60%)"
          : "transparent",
      }}
    >
      <div
        onClick={onToggle}
        style={{
          display: "grid",
          gridTemplateColumns: "60px 1fr 220px 100px 70px 30px",
          gap: 14,
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              color: leg.anchor ? "var(--sa-accent-saffron)" : "var(--sa-fg-muted)",
              letterSpacing: "0.18em",
            }}
          >
            L{String(index + 1).padStart(2, "0")}
          </div>
          {leg.anchor ? (
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 9,
                color: "var(--sa-accent-saffron)",
                letterSpacing: "0.14em",
                marginTop: 4,
              }}
            >
              ANCHOR
            </div>
          ) : null}
          {leg.flagged ? (
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 9,
                color: "var(--sa-signal-warn)",
                letterSpacing: "0.14em",
                marginTop: 4,
              }}
            >
              ⚠ FLAG
            </div>
          ) : null}
        </div>

        <div>
          <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            {leg.category ? <CategoryChip category={leg.category} /> : null}
            {leg.layer ? (
              <span
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 9,
                  letterSpacing: "0.16em",
                  color: tone,
                  padding: "2px 7px",
                }}
              >
                {leg.layer}
              </span>
            ) : null}
            {leg.correlated_with.length > 0 ? (
              <span
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 9,
                  letterSpacing: "0.16em",
                  color: "var(--sa-fg-muted)",
                  padding: "2px 7px",
                }}
              >
                ↔ L{leg.correlated_with.map((n) => String(n).padStart(2, "0")).join(" L")}
              </span>
            ) : null}
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-body)",
              fontSize: 16,
              color: "var(--sa-fg-primary)",
              fontWeight: 500,
            }}
          >
            {leg.market}{" "}
            <span style={{ color: "var(--sa-fg-muted)" }}>→</span>{" "}
            <span style={{ color: "var(--sa-accent-saffron)" }}>{leg.selection}</span>
          </div>
          {leg.reasoning_short ? (
            <div
              style={{
                fontFamily: "var(--sa-font-body)",
                fontSize: 13,
                color: "var(--sa-fg-secondary)",
                marginTop: 6,
                lineHeight: 1.55,
              }}
            >
              {summarizeReasoning(leg.reasoning_short)}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
            }}
          >
            <span
              style={{
                color: "var(--sa-fg-muted)",
                letterSpacing: "0.12em",
                fontSize: 9.5,
              }}
            >
              MODEL · MARKET
            </span>
            {leg.edge_pct !== null ? (
              <span style={{ color: "var(--sa-accent-saffron)" }}>+{leg.edge_pct}pp</span>
            ) : null}
          </div>
          <div
            style={{
              height: 4,
              background: "var(--sa-hairline-bright)",
              borderRadius: 1,
              overflow: "hidden",
              position: "relative",
            }}
          >
            {leg.market_prob !== null ? (
              <div
                style={{
                  position: "absolute",
                  left: `${leg.market_prob}%`,
                  top: -3,
                  bottom: -3,
                  width: 1,
                  background: "var(--sa-fg-primary)",
                }}
              />
            ) : null}
            {leg.model_prob !== null ? (
              <div
                style={{
                  height: "100%",
                  width: `${leg.model_prob}%`,
                  background: "var(--sa-accent-saffron)",
                }}
              />
            ) : null}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              color: "var(--sa-fg-muted)",
            }}
          >
            <span>{leg.model_prob ?? "—"}%</span>
            <span>{leg.market_prob ?? "—"}%</span>
          </div>
        </div>

        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color:
              (leg.legConfidence ?? 0) >= 75
                ? "var(--sa-signal-green)"
                : "var(--sa-accent-saffron)",
            textAlign: "right",
          }}
        >
          {leg.legConfidence ?? "—"}
          {leg.legConfidenceLabel ? ` · ${leg.legConfidenceLabel}` : ""}
        </div>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 16,
            color: "var(--sa-fg-primary)",
            textAlign: "right",
          }}
        >
          {leg.odds.toFixed(2)}×
        </div>
        <div style={{ color: "var(--sa-fg-muted)", textAlign: "right" }}>
          {isOpen ? "−" : "+"}
        </div>
      </div>

      {isOpen && reasoningLong ? (
        <div
          style={{
            marginTop: 14,
            padding: "16px 18px",
            background: "var(--sa-bg-raised)",
            borderLeft: "2px solid var(--sa-accent-saffron)",
            borderRadius: 2,
          }}
        >
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              color: "var(--sa-accent-saffron)",
              letterSpacing: "0.16em",
              marginBottom: 8,
            }}
          >
            FULL REASONING
          </div>
          <p
            style={{
              fontFamily: "var(--sa-font-body)",
              fontSize: 13.5,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.65,
              margin: 0,
            }}
          >
            {reasoningLong}
          </p>
          {leg.flagged ? (
            <div style={{ marginTop: 10 }}>
              <Badge variant="warn">⚠ ADVERSARIAL FLAG · see Adversarial Review below</Badge>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
